import { periodDateUTC } from './freshness.ts'
import { formatBytes, relativeTime } from './general.ts'
import { PROVISIONED_STOP_CLAUSE } from './computeStop.ts'
import type { BillingInterval, PlanRow } from '../models/useBilling'

/**
 * The flat plan fee that belongs in the period's bill panel — app#264.
 *
 * ⚠⚠ ZERO FOR AN ANNUAL SUBSCRIPTION. The year is charged in full at checkout, so no month of it
 * carries the monthly fee, and the month projects overage alone. `plans.monthly_fee_cents` is the
 * only fee the row carries, and it was added to every month of an annual term.
 *
 * ⚠⚠ ZERO FOR A PAST PERIOD, and that is a decision about what the panel can know. The fee it
 * holds is the plan of TODAY, and the picker can show a month the organization was on another
 * plan, or did not exist. Nothing here reconstructs plan history: the panel prints the usage drawn
 * in that month and sends the reader to the invoices for the fee, and it renames itself to match.
 *
 * ⚠ A `null` interval keeps the monthly fee. An older router, a reader who cannot load the
 * summary, and the instant before the summary lands all read exactly as they did before the field.
 */
export const flatBaseCents = (
  monthlyFeeCents: number,
  interval: BillingInterval | null,
  period: string,
  currentPeriod: string
): number => {
  if (period < currentPeriod) return 0
  if (interval === 'year') return 0
  return monthlyFeeCents
}

// What the billing panel may say about a subscription: the verb, how the date beside it is
// written, what the end of that subscription costs, and which of it this reader may be told.
//
// ⚠⚠ THE STATUS ALONE CANNOT ANSWER THIS. Stripe keeps a cancelled subscription `active` until the
// period it has been paid for actually ends, so a line keyed on `status === 'active'` told a
// customer who had just cancelled that their plan RENEWS — for up to a month, about their own
// money. `cancel_at_period_end` is the field that separates the two, and the router serves it
// (router#1133).
//
// Returns 'renews' when the date is the next charge, 'ends' when it is the last day of service,
// and null when there is no date worth printing.
export type PeriodVerb = 'renews' | 'ends' | null

export const periodVerb = (
  status: string,
  cancelAtPeriodEnd: boolean,
  currentPeriodEnd: string | null,
  hardCaps: boolean | null
): PeriodVerb => {
  if (!currentPeriodEnd) return null
  // ⚠ A hard-caps plan (Free) has no period to renew or end. It can carry a stale
  // `current_period_end` — DowngradeOrgToFree does not clear it — so printing either verb would
  // announce a billing event for an org that has no subscription at all.
  if (hardCaps) return null
  if (status === 'active') return cancelAtPeriodEnd ? 'ends' : 'renews'
  if (status === 'canceled') return 'ends'
  // past_due and unpaid keep their date out of this line: the banner above already owns that
  // state, and "renews" beside a failed payment is a promise nothing can keep.
  return null
}

/**
 * What the Stripe Checkout return should say, or null when this render is not one (app#247).
 *
 * ⚠⚠ THREE STATES, ONE DECISION. `finalizing` was the only one the panel had, and it is the
 * TRANSIENT one: it covers the wait between Stripe redirecting and the webhook landing, then goes
 * false and nothing took its place. So the whole acknowledgement that money had moved was that the
 * plan chip now read PRO, and `status=cancel` — read into the same variable — was never used at
 * all. A customer who abandoned Checkout got no word on whether they had been charged.
 *
 * ⚠ `null` FOR AN ORDINARY VISIT is most of the time. The parameter is only on the URL for the one
 * navigation Stripe performs, so every other render of this panel must say nothing.
 *
 * ⚠ AN UNKNOWN PARAMETER VALUE IS AN ORDINARY VISIT, not an error. `?status=` is a public URL a
 * customer can edit, and a page that renders a money statement because a query string said so is a
 * page anybody can make say anything.
 */
export type CheckoutOutcome = 'finalizing' | 'subscribed' | 'cancelled' | null

export const checkoutOutcome = (
  checkoutReturn: string | null,
  status: string
): CheckoutOutcome => {
  if (checkoutReturn === 'cancel') return 'cancelled'
  if (checkoutReturn !== 'success') return null
  // ⚠⚠ `active` IS THE WEBHOOK'S OWN SIGNAL AND IS WHAT SEPARATES THESE TWO. It is set by the
  // subscription webhook, so by the time it is true the plan on the row is the plan that was
  // bought — which is why the subscribed copy may name the plan and must not name an amount.
  return status === 'active' ? 'subscribed' : 'finalizing'
}

// The date beside the verb: "1 October (UTC) · in 28 days".
//
// ⚠⚠ AN ABSOLUTE DATE FIRST, AND THE ZONE ON IT — THIS IS app#208. The line printed the relative
// form alone ("renews in 28 days"), which a customer cannot check against an invoice or against
// Stripe's portal without doing arithmetic, and which loses precision as it rounds. Measured on
// prod 2026-09-03: this line said "in 28 days", the portal said "September 30, 2026", and
// `current_period_end` was `2026-10-01 00:00:00+00` — one instant, two zones, two dates in front
// of a customer deciding whether to cancel.
//
// ⚠ The relative form is CARRIED, not replaced. It is the warmer half and it answers "how long
// have I got" without arithmetic; it just cannot be the only half.
//
// ⚠⚠ `periodDateUTC` IS SHARED WITH `resetLabel` DELIBERATELY. At least three surfaces print this
// same instant — this line, the credit block's reset label and the usage rings' reset line — and
// before app#208 they agreed by coincidence, each formatting it themselves. One formatter is what
// stops them drifting.
//
// ⚠ AN ANNUAL PLAN GETS A YEAR, AND THIS LINE DOES NOT ASK FOR IT — app#225. The boundary of an
// annual subscription falls in a year the reader is not in, and the date alone then reads as a
// date in the past. `periodDateUTC` adds the year whenever the boundary year is not the current
// UTC year, so the rule stays in the one formatter and the monthly rings never reach it.
//
// ⚠ Returns '' for a missing or unparseable stamp, so the caller prints the verb with no date
// rather than "renews Invalid Date". The caller already guards on the stamp being present; this
// does not rely on that.
export const periodDateLabel = (currentPeriodEnd: string | null | undefined): string => {
  if (!currentPeriodEnd) return ''
  const date = periodDateUTC(currentPeriodEnd)
  if (!date) return ''
  const relative = relativeTime(currentPeriodEnd)
  return relative ? `${date} · ${relative}` : date
}

/**
 * What lands on the organization when a subscription ends — the half the cancellation copy left
 * out, and app#210.
 *
 * ⚠⚠ NAME THE LOSS AS WELL AS WHAT IS KEPT. "Nothing is deleted when it ends — your devices,
 * files, names and permissions stay" is true, and it was the whole paragraph. The consequence it
 * omits is that the organization lands on FREE, where the allowances are smaller and the regime
 * past them is different: transfers STOP rather than being billed, uploads are refused, and a
 * machine noBGP runs for you is stopped. The Free card two inches up the same page explains all
 * of it — but a customer reads that card while SHOPPING and reads this paragraph while DECIDING
 * TO CANCEL. Same gap as app#159 and app#160.
 *
 * ⚠⚠ EVERY NUMBER COMES FROM THE PLAN ROW. Writing "50 GB" here would be a second copy of a
 * number the `plans` table already holds, which is how the Business compute constant went wrong.
 *
 * ⚠ COMPUTE HAS NO NUMBER HERE, AND THAT IS THE SAME RULE RATHER THAN AN EXCEPTION. Included
 * compute is a model constant and NOT a `plans` column (see `Billing.tsx`), so there is no row to
 * read it from — and quoting the hours from a constant is exactly the second copy this avoids.
 * The REGIME is what the sentence needs, and the regime is in the row's `hard_caps`.
 *
 * ⚠⚠ "NODES STAY ONLINE" IS SCOPED TO THE BANDWIDTH CLAUSE, and that is app#163's rule rather
 * than a wording preference. The promise is true for bandwidth and FALSE for compute — a machine
 * noBGP runs for you is ended by the compute stop, and the node with it. Led with unscoped, the
 * paragraph contradicts its own last sentence. The Free plan card words it the same way.
 *
 * ⚠ NO RETENTION WINDOW FOR A STOPPED MACHINE. `PROVISIONED_STOP_CLAUSE` is shared with the
 * banner and the plan cards for that reason: nobgp-router#1085 deletes the 48-hour ladder, so a
 * number here would ship already scheduled for removal. The word "suspended" is not used
 * anywhere in it either (router#1078).
 *
 * ⚠ Returns '' when the Free row cannot answer — not loaded, not metered, or carrying a
 * non-positive allowance, where `0` is the "unlimited" convention on other columns and would
 * print "0 B of bandwidth a month". Then only the reassurance renders, which is what the panel
 * says today. Saying nothing beats saying something wrong about a customer's money.
 */
export const freePlanConsequence = (free: PlanRow | null): string => {
  if (!free || !free.metered) return ''
  if (free.bandwidthBytesFree <= 0 || free.storageBytesFree <= 0) return ''
  return (
    `The organization then moves to Free. Free includes ${formatBytes(free.bandwidthBytesFree)} of ` +
    `bandwidth a month and ${formatBytes(free.storageBytesFree)} of storage. Past the bandwidth ` +
    `allowance your nodes stay online and files stay put, but transfers stop until the next ` +
    `billing period — never a bill. Past the storage limit new uploads are refused. Past Free's ` +
    `included compute, ${PROVISIONED_STOP_CLAUSE}.`
  )
}

/**
 * Which of the account detail this reader may be TOLD, in three states — app#216.
 *
 * ⚠⚠ AN UNFETCHED VALUE AND AN EMPTY RESULT ARE THE SAME VALUE, AND THE PANEL READ THEM AS ONE.
 * The summary read is Owner-only, so for an admin or a member of a PAID org it never runs: `card`
 * stays null and `invoices` stays empty, and the panel printed "No default payment method on file"
 * and "No invoices yet" to an organization holding a Visa and two paid invoices. `CreditBlock`
 * already had this right — `showsCredit` draws no conclusion from a null position.
 *
 * ⚠⚠ THE FIX IS NOT TO FETCH FOR THEM. The router refuses the read (ActionManageBilling is
 * Owner-only), and the refusal would then be the only thing that changed on their screen. The
 * gate in `Billing.tsx` is correct; what was wrong is what this panel concluded from it.
 *
 * ⚠ `summaryLoaded` IS SAFE TO READ AS "never attempted" because both pages call `clearSummary()`
 * on every org switch, before anything is fetched. Without that it would say true from the previous org and
 * an admin would read an org's money that is not the one on screen.
 *
 * ⚠ 'loading' IS A THIRD STATE AND NOT A ROUNDING OF THE OTHER TWO. An Owner whose read is still
 * in flight has `summaryLoaded` false as well, and telling THEM that the Owner can see it is the
 * mirror of the same defect. They are told nothing for that instant.
 */
export type SummaryVisibility =
  | 'shown' // the read landed: an empty result really is empty
  | 'loading' // the reader may see it and the read has not answered yet
  | 'restricted' // the read never ran, and never will for this reader

export const summaryVisibility = (summaryLoaded: boolean, canManage: boolean): SummaryVisibility => {
  if (summaryLoaded) return 'shown'
  return canManage ? 'loading' : 'restricted'
}
